import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fixedUi } from '@/i18n/fixedUi';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { normalizeTemuShopTypeSlug } from '@/temu-platform/constants/temuRoutes';

export const TEMU_GATEWAY_REGION_ALL = 'ALL';

export const TEMU_GATEWAY_REGION_OPTIONS = [
  { value: 'ALL', label: '全部' },
  { value: 'US', label: '美区' },
  { value: 'EU', label: '欧区' },
  { value: 'GLOBAL', label: '全球' },
];

/**
 * 半托 / 本土网关区页签：全部 / 美区 / 欧区 / 全球。
 * 全托管履约与商品均走 CN，不展示区筛选。
 *
 * @param {boolean} [alwaysEnable] 产品模板等按「创建所选站点」分区时传 true，
 *   不按销售订单 Token / 开站禁用区 Tab。
 */
export default function TemuGatewayRegionTabs({
  shopId,
  value = TEMU_GATEWAY_REGION_ALL,
  onChange,
  className = '',
  regions: regionsProp,
  alwaysEnable = false,
  hideForFull = true,
  ariaLabel = fixedUi('网关区域'),
}) {
  const { shopType } = useParams();
  const slug = normalizeTemuShopTypeSlug(shopType);
  const [regions, setRegions] = useState(() => regionsProp || null);

  useEffect(() => {
    if (hideForFull && slug === 'full') {
      setRegions(null);
      return undefined;
    }
    if (alwaysEnable) {
      setRegions([
        { code: 'US', enabled: true },
        { code: 'EU', enabled: true },
        { code: 'GLOBAL', enabled: true },
      ]);
      return undefined;
    }
    if (regionsProp) {
      setRegions(regionsProp);
      return undefined;
    }
    if (!shopId) {
      setRegions(null);
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getSalesOrderRegions?.(shopId)
      .then((res) => {
        if (cancelled) return;
        setRegions(Array.isArray(res?.regions) ? res.regions : res?.items || []);
      })
      .catch(() => {
        if (!cancelled) setRegions([]);
      });
    return () => { cancelled = true; };
  }, [shopId, regionsProp, slug, alwaysEnable, hideForFull]);

  const enabledByCode = useMemo(() => {
    const map = new Map();
    (regions || []).forEach((r) => {
      const code = String(r?.code || r?.region || '').toUpperCase();
      if (!code) return;
      map.set(code, r?.enabled !== false);
    });
    return map;
  }, [regions]);

  if (hideForFull && slug === 'full') {
    return null;
  }

  return (
    <div
      className={['tpo-status-tabs', 'tpo-status-tabs--gateway-region', className].filter(Boolean).join(' ')}
      role="tablist"
      aria-label={ariaLabel}
    >
      {TEMU_GATEWAY_REGION_OPTIONS.map((opt) => {
        const isAll = opt.value === 'ALL';
        const enabled = alwaysEnable
          || isAll
          || enabledByCode.size === 0
          || enabledByCode.get(opt.value) !== false;
        const active = String(value || 'ALL').toUpperCase() === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={!enabled}
            title={!enabled ? fixedUi('未开站或缺少对应区 Token') : undefined}
            className={`tpo-status-tab${active ? ' tpo-status-tab--active' : ''}${!enabled ? ' tpo-status-tab--disabled' : ''}`}
            onClick={() => {
              if (!enabled || typeof onChange !== 'function') return;
              onChange(opt.value);
            }}
          >
            {fixedUi(opt.label)}
          </button>
        );
      })}
    </div>
  );
}
